import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import { httpClient } from '@/core/api/http-client';
import { useAppStore } from '@/core/store/app.store';
import { toIso2 } from '@/core/i18n/country-code';

import type { LogoutOptions } from '@auth0/auth0-react';
import type { PropsWithChildren } from 'react';

/**
 * Registers a response interceptor that surfaces global HTTP errors as toasts
 * and forces logout on 401 Unauthorized.
 */
const registerResponseErrorToasts = (logout: (options?: LogoutOptions) => Promise<void> | void) => {
  return httpClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!error.response) {
        toast.error('Network Error: Please check your internet connection.');
      } else if (error.response.status === 401) {
        toast.error('Session expired. Please log in again.');
        logout({ logoutParams: { returnTo: window.location.origin } });
      } else if (error.response.status === 403) {
        toast.error('Forbidden: You do not have permission to perform this action.');
      }
      return Promise.reject(error);
    },
  );
};

/**
 * Provider that attaches Auth0 tokens to the global Axios HTTP client and
 * registers global response error toasts.
 *
 * Decouples the pure `httpClient` from React Context so non-React services
 * (TanStack Query, etc.) can keep using the client transparently.
 */
export const HttpInterceptorProvider = (props: PropsWithChildren) => {
  const { getAccessTokenSilently, logout } = useAuth0();

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    const request_interceptor_id = httpClient.interceptors.request.use(async (config) => {
      try {
        const access_token = await getAccessTokenSilently();
        config.headers.Authorization = `Bearer ${access_token}`;

        // Set Country Context (ISO2 format for the backend)
        const iso3 = useAppStore.getState().country;
        config.headers['X-Country-Code'] = toIso2(iso3);
      } catch (error: unknown) {
        const err = error as Record<string, unknown>;
        const error_code = err?.error || err?.message;

        if (
          error_code === 'login_required' ||
          error_code === 'invalid_grant' ||
          error_code === 'consent_required'
        ) {
          console.warn('Session invalid, forcing logout:', error);
          handleLogout();
          return Promise.reject(error);
        }

        console.warn('Unable to get access token silently:', error);
      }
      return config;
    });

    return () => {
      httpClient.interceptors.request.eject(request_interceptor_id);
    };
  }, [getAccessTokenSilently, handleLogout]);

  useEffect(() => {
    const response_interceptor_id = registerResponseErrorToasts(handleLogout);

    return () => {
      httpClient.interceptors.response.eject(response_interceptor_id);
    };
  }, [handleLogout]);

  return <>{props.children}</>;
};
